<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import { usePoleDevStore, type PoleDevRequest } from '@/stores/usePoleDevStore'

const store = usePoleDevStore()

const candidates = ref<PoleDevRequest[]>([])
const selectedIds = ref<string[]>([])

const newRecipientEmail = ref('')
const newRecipientName = ref('')
const recipients = ref<{ email: string; name?: string }[]>([])

const subject = ref('Mise à jour pôle dev — demandes livrées')
const templateCode = ref('POLEDEV_COMMUNICATION_LIVREES')

const result = ref<null | {
  sent: string[]
  errors: { email: string; error: string }[]
  ignored: { code: string; status: string }[]
  processedRequestCodes: string[]
}>(null)

const allSelected = computed(() => candidates.value.length > 0 && selectedIds.value.length === candidates.value.length)

function toggleAll() {
  selectedIds.value = allSelected.value ? [] : candidates.value.map((c) => c._id)
}

async function load() {
  candidates.value = await store.fetchReadyForCommunication()
  // Tout coché par défaut
  selectedIds.value = candidates.value.map((c) => c._id)
}

function addRecipient() {
  const email = newRecipientEmail.value.trim()
  if (!email) return
  if (recipients.value.some((r) => r.email.toLowerCase() === email.toLowerCase())) {
    newRecipientEmail.value = ''
    newRecipientName.value = ''
    return
  }
  recipients.value.push({ email, name: newRecipientName.value.trim() || undefined })
  newRecipientEmail.value = ''
  newRecipientName.value = ''
}
function removeRecipient(email: string) {
  recipients.value = recipients.value.filter((r) => r.email !== email)
}

async function submit() {
  result.value = null
  const res = await store.sendCommunication({
    requestIds: selectedIds.value,
    recipients: recipients.value,
    subject: subject.value || undefined,
    templateCode: templateCode.value || undefined,
  })
  result.value = res
  await load()
}

onMounted(load)
</script>

<template>
  <div class="p-6 flex flex-col gap-6">
    <div class="flex items-center justify-between flex-wrap gap-3">
      <Bs2Title title="Pôle dev — communications" subtitle="Préparer un envoi groupé pour les demandes livrées" />
      <button class="btn btn-sm" :disabled="store.loading" @click="load">Recharger</button>
    </div>

    <!-- Liste demandes éligibles -->
    <div class="card bg-base-100 shadow p-4">
      <div class="flex items-center justify-between mb-3">
        <h3 class="font-semibold">Demandes livrées à communiquer ({{ candidates.length }})</h3>
        <button v-if="candidates.length > 0" class="btn btn-xs" @click="toggleAll">
          {{ allSelected ? 'Tout décocher' : 'Tout cocher' }}
        </button>
      </div>

      <div v-if="store.loading && candidates.length === 0" class="text-sm text-base-content/60 py-6 text-center">Chargement…</div>
      <div v-else-if="candidates.length === 0" class="text-sm text-base-content/60 py-6 text-center">
        Aucune demande au statut LIVRÉE en attente de communication.
      </div>
      <div v-else class="overflow-x-auto">
        <table class="table table-sm">
          <thead>
            <tr>
              <th></th>
              <th>Code</th>
              <th>Titre</th>
              <th>Heures</th>
              <th>Livrée le</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in candidates" :key="r._id">
              <td>
                <input type="checkbox" :value="r._id" v-model="selectedIds" class="checkbox checkbox-sm" />
              </td>
              <td class="font-mono text-xs">{{ r.code }}</td>
              <td class="max-w-md truncate">{{ r.title }}</td>
              <td>{{ r.estimatedHours ?? '—' }}</td>
              <td class="text-xs">{{ new Date(r.updatedAt).toLocaleDateString('fr-FR') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Destinataires + template -->
    <div class="card bg-base-100 shadow p-4">
      <h3 class="font-semibold mb-3">Destinataires</h3>
      <div class="flex flex-wrap gap-2 mb-3">
        <span
          v-for="r in recipients"
          :key="r.email"
          class="badge badge-outline gap-1"
        >
          {{ r.name ? `${r.name} <${r.email}>` : r.email }}
          <button type="button" class="text-base-content/60 hover:text-error" @click="removeRecipient(r.email)">×</button>
        </span>
        <span v-if="recipients.length === 0" class="text-sm text-base-content/60 italic">Aucun destinataire ajouté.</span>
      </div>
      <div class="flex gap-2 flex-wrap">
        <input
          v-model="newRecipientEmail"
          type="email"
          class="input input-bordered input-sm w-64"
          placeholder="email@…"
          @keydown.enter.prevent="addRecipient"
        />
        <input v-model="newRecipientName" type="text" class="input input-bordered input-sm w-48" placeholder="Nom (optionnel)" />
        <button class="btn btn-sm" :disabled="!newRecipientEmail.trim()" @click="addRecipient">Ajouter</button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <label class="form-control">
          <span class="label-text text-xs mb-1">Objet du mail</span>
          <input v-model="subject" type="text" class="input input-bordered input-sm w-full" />
        </label>
        <label class="form-control">
          <span class="label-text text-xs mb-1">Code template Brevo</span>
          <input v-model="templateCode" type="text" class="input input-bordered input-sm w-full" />
        </label>
      </div>
    </div>

    <!-- Submit -->
    <div class="flex justify-end">
      <button
        class="btn btn-primary"
        :disabled="store.loading || selectedIds.length === 0 || recipients.length === 0"
        @click="submit"
      >
        Envoyer la communication ({{ selectedIds.length }} demande(s) → {{ recipients.length }} destinataire(s))
      </button>
    </div>

    <!-- Résultat -->
    <div v-if="result" class="card bg-base-100 shadow p-4">
      <h3 class="font-semibold mb-2">Résultat de l'envoi</h3>
      <div v-if="result.sent.length > 0" class="text-sm text-success mb-2">
        Envoyé à : {{ result.sent.join(', ') }}
      </div>
      <div v-if="result.processedRequestCodes.length > 0" class="text-sm mb-2">
        Demandes basculées en COMMUNICATION_FAITE : {{ result.processedRequestCodes.join(', ') }}
      </div>
      <div v-if="result.errors.length > 0" class="text-sm text-error">
        <div class="font-semibold">Erreurs :</div>
        <ul class="list-disc list-inside">
          <li v-for="(e, i) in result.errors" :key="i">{{ e.email }} — {{ e.error }}</li>
        </ul>
      </div>
      <div v-if="result.ignored.length > 0" class="text-sm text-base-content/60">
        Ignorées (statut différent de LIVRÉE) : {{ result.ignored.map((i) => `${i.code} (${i.status})`).join(', ') }}
      </div>
    </div>
  </div>
</template>
